import React, { useEffect, useRef } from "react";

/**
 * @typedef {Object} SubjectProgressCircleProps
 * @property {string} subject - Subject name
 * @property {number} percentage - Completion percentage
 * @property {"green" | "purple" | "red"} color - Color theme for the progress circle
 */

/**
 * Displays a circular progress indicator for a subject
 * @param {SubjectProgressCircleProps} props
 */
export default function SubjectProgressCircle({ subject, percentage, color }) {
  const canvasRef = useRef(null);

  const colorMap = {
    green: { bg: "#e6f7e6", stroke: "#4ade80" },
    purple: { bg: "#f3e8ff", stroke: "#a855f7" },
    red: { bg: "#fee2e2", stroke: "#ef4444" },
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // For high-resolution displays
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const radius = Math.min(centerX, centerY) - 10;

    // Background circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = colorMap[color].bg;
    ctx.lineWidth = 8;
    ctx.stroke();

    // Progress arc
    const startAngle = -0.5 * Math.PI; // Start at top
    const endAngle = startAngle + (percentage / 100) * 2 * Math.PI;

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, startAngle, endAngle);
    ctx.strokeStyle = colorMap[color].stroke;
    ctx.lineWidth = 8;
    ctx.stroke();

    // Draw dashed segments
    const segments = 40;
    const dashLength = (2 * Math.PI * radius) / segments;
    const gapLength = dashLength * 0.3;

    ctx.beginPath();
    for (let i = 0; i < segments; i++) {
      const angle = startAngle + (i / segments) * 2 * Math.PI;
      const x1 = centerX + Math.cos(angle) * (radius - 4);
      const y1 = centerY + Math.sin(angle) * (radius - 4);
      const x2 = centerX + Math.cos(angle) * (radius + 4);
      const y2 = centerY + Math.sin(angle) * (radius + 4);

      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
    }
    ctx.strokeStyle = colorMap[color].bg;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Progress arc with dashes
    ctx.beginPath();
    for (let i = 0; i < segments * (percentage / 100); i++) {
      const angle = startAngle + (i / segments) * 2 * Math.PI;
      const x1 = centerX + Math.cos(angle) * (radius - 4);
      const y1 = centerY + Math.sin(angle) * (radius - 4);
      const x2 = centerX + Math.cos(angle) * (radius + 4);
      const y2 = centerY + Math.sin(angle) * (radius + 4);

      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
    }
    ctx.strokeStyle = colorMap[color].stroke;
    ctx.lineWidth = 1;
    ctx.stroke();
  }, [percentage, color]);

  return (
    <div className="flex flex-col items-start">
      <div className="relative w-32 h-32">
        <canvas ref={canvasRef} className="w-full h-full"></canvas>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold">{percentage}%</span>
        </div>
      </div>
      <span className="text-sm mt-2 font-medium">{subject}</span>
    </div>
  );
}